import colors from './colors'
import generateMixins from 'shared/helpers/generateMixins'
export type DefaultTheme = typeof defaultTheme
export type CoreTheme = typeof coreDefaultTheme | typeof coreCustomTheme
import generateShadows from 'helpers/generateShadows'
import generateMediaQueries from 'shared/helpers/generateMediaQueries'
import { fontStyle, coreTypography } from 'assets/theme/typography'
import layout from './layout'

type WidgetWidth = { widget: number }

// This is a special case only used to determining the widget max-width.
const widgetWidth: WidgetWidth = { widget: 720 }

export type Breakpoints = Record<
  'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'xxxl',
  number
> &
  WidgetWidth

export const breakpoints: Breakpoints = {
  xs: 320,
  sm: 480,
  md: 760,
  lg: 1024, // Desktop
  xl: 1600,
  xxl: 1400,
  xxxl: 1920,
  ...widgetWidth
}

export type ContentMaxWidth = {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  [keys in 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl']: number
} & WidgetWidth

const contentMaxWidth: ContentMaxWidth = {
  xxs: 200,
  xs: 400,
  sm: 480,
  md: 600,
  lg: 780,
  xl: 1080,
  xxl: 1600,
  ...widgetWidth
}

const shadows = generateShadows('#000')

/**
 * @desc core values for setting up the theme
 */
export const coreTheme = {
  initialColorModeName: 'light',
  ...coreTypography,
  fontStyle,
  space: [0, 4, 8, 12, 16, 24, 32, 40, 48, 64, 80, 96, 128, 160],
  transitionTime: [0, 0.2], // seconds
  radii: [0, 2, 4, 8, 16, 32],
  border: `1px solid ${colors.border.primary}`,
  shadows,
  // Centralize all values in one place to be easy to confirm what has priority
  zIndex: {
    header: 100,
    sidebar: 99,
    bottomBar: 98,
    buttonPopup: 8,
    tableFirstCell: 7, // Top leftmost cell, with other content moving below
    tableHeaderCell: 6, // First row
    tableFirstColumnCell: 5 // First column
  },
  breakpoints,
  mediaQueries: generateMediaQueries(breakpoints),
  contentMaxWidth,
  layout,
  modal: {
    shadow: shadows[2],
    margin: 5 // Represents theme.space[5]
  },
  // TODO: Readdress in #5032 - currently depended on by ViewSwitch
  header: {
    height: 0
  },
  input: {
    height: 48
  }
}

const coreDefaultTheme = {
  colors,
  ...coreTheme
}

/**
 * @desc exports theme using the default values
 */
export const defaultTheme = {
  ...coreDefaultTheme,
  ...generateMixins(coreDefaultTheme)
}

const coreCustomTheme = {
  colors: {
    ...coreDefaultTheme.colors,
    primary: '#096C00'
  },
  ...coreTheme
}

/**
 * @desc exports theme using custom colors
 */
export const customTheme: DefaultTheme = {
  ...coreCustomTheme,
  ...generateMixins(coreCustomTheme)
}
